import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { Arrow } from "@sol/icons";
import { Route, useRouter } from "@sol/routing";
import { Switch } from "@sol/uikit/data-entry/Switch";
import { Button } from "@sol/uikit/general/Button";

import { getGDPRState } from "../../store/gdpr/selector";
import { IGDPRSettings } from "../../store/gdpr/types";
import Text from "../../uikit/Text";
import Markdown from "../Markdown";

const GDPRSettingsSwitch = styled(props => {
    const { checked, className, "data-cy": dataCy, ...rest } = props;
    const [t] = useTranslation();

    return (
        <label className={className} data-cy={dataCy} htmlFor={rest.id}>
            <Switch {...rest} checked={checked} />
            <Text as="span">{t(`component.GDPRDisclaimer.switch.${checked ? "active" : "inactive"}`)}</Text>
        </label>
    );
})`
    display: flex;
    flex-direction: row;
    align-items: center;

    ${Text} {
        margin-left: 9px;
    }
`;

type GDPRDisclaimerProps = {
    className?: string;
    onSave: (setting?: IGDPRSettings) => void;
};

const GDPRDisclaimer = ({ className, onSave }: GDPRDisclaimerProps) => {
    const [t] = useTranslation();
    const router = useRouter();

    const gdpr = useSelector(getGDPRState);
    const [showSettings, setShowSettings] = useState(false);
    const [analytics, setAnalytics] = useState(!!gdpr.settings.analytics);

    const displaySettings = () => setShowSettings(true);

    const toggleAnalyticsOptIn = () => setAnalytics(!analytics);

    const handleSubmit = () => onSave && onSave({ analytics });

    const handleBack = useCallback(() => {
        if (!gdpr.initial) {
            // Not initial gdpr configuation
            if (router.canGoBack) {
                router.back();
            } else {
                router.push({ route: Route.HOME });
            }
        } else {
            setShowSettings(false);
        }
    }, [setShowSettings, router, gdpr.initial]);

    return showSettings || !gdpr.initial ? (
        <div data-cy="gdpr-settings" className={className}>
            <Text as="h1" variant="h3">
                {t("component.GDPRDisclaimer.settings.title")}
            </Text>

            <Text as="h2" variant="label">
                {t("component.GDPRDisclaimer.settings.appData.title")}
            </Text>
            <GDPRSettingsSwitch checked disabled />
            <Text>{t("component.GDPRDisclaimer.settings.appData.body")}</Text>

            <Text as="h2" variant="label" id="legend-analytics">
                {t("component.GDPRDisclaimer.settings.analytics.title")}
            </Text>
            <GDPRSettingsSwitch
                data-cy="analytics"
                checked={analytics}
                onChange={toggleAnalyticsOptIn}
                id="analytics-switch"
                aria-describedby="legend-analytics"
                autoFocus
            />
            <Text>{t("component.GDPRDisclaimer.settings.analytics.body")}</Text>

            <div className={"flex w-full justify-between"}>
                <Button type="primary" size="large" onClick={handleSubmit} data-cy="gdpr-save">
                    {t("component.GDPRDisclaimer.settings.submit")}
                </Button>
                <Button htmlType="button" size="large" onClick={handleBack} type="default" data-cy="gdpr-back">
                    <Arrow direction="left" aria-hidden="true" /> {t("component.GDPRDisclaimer.settings.back")}
                </Button>
            </div>
        </div>
    ) : (
        <div data-cy="gdpr-disclaimer" className={className}>
            <Text as="h1" variant="h3">
                {t("component.GDPRDisclaimer.title")}
            </Text>
            <Markdown markdown={t("component.GDPRDisclaimer.body")}></Markdown>
            <div className={"flex w-full justify-between"}>
                <Button type="primary" size="large" onClick={handleSubmit} data-cy="gdpr-accept">
                    {t("component.GDPRDisclaimer.accept")}
                </Button>
                <Button type="default" size="large" onClick={displaySettings} data-cy="gdpr-manage">
                    {t("component.GDPRDisclaimer.manage")}
                </Button>
            </div>
        </div>
    );
};

export default styled(GDPRDisclaimer)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    & > ${Text} {
        margin-bottom: 1em;
    }

    ${GDPRSettingsSwitch} {
        margin-bottom: 1em;
    }
`;
